import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import "../styles/VideosPage.css";
import { motion } from "framer-motion";

const VideosPage = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const videosPerPage = 20;

  const PageLoader = () => {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#ffffff",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <div
        className="spinner-border"
        role="status"
        style={{ width: "3.5rem", height: "3.5rem", color: "#f64a1aff" }}
      />
      <p className="mt-3 fw-semibold">Loading Porvaall Videos...</p>
    </div>
  );
};


  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}video_list.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        const data = await response.json();
        if (data.head.code === 200) {
          const videoData = data.body.map((v) => ({
            id: v.video_id,
            title: v.video_title,
            videoUrl: getEmbedUrl(v.video_url),
            thumbnail: `https://img.youtube.com/vi/${extractYouTubeId(v.video_url)}/hqdefault.jpg`,
          }));
          setVideos(videoData);
        } else {
          setVideos([]);
        }
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [API_BASE_URL]);

  const getEmbedUrl = (url) => {
    const regex = /(?:\?v=|\/embed\/|\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const extractYouTubeId = (url) => {
    const regex = /(?:\?v=|\/embed\/|\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : "";
  };

  // Pagination logic
  const indexOfLastVideo = currentPage * videosPerPage;
  const indexOfFirstVideo = indexOfLastVideo - videosPerPage;
  const currentVideos = videos.slice(indexOfFirstVideo, indexOfLastVideo);
  const totalPages = Math.ceil(videos.length / videosPerPage);

  const goToPage = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <PageLoader />;

  return (
    <div className="videospagebanner">
      {/* HERO SECTION */}
      <motion.section
        className="text-center d-flex align-items-center justify-content-center"
        style={{ minHeight: "20vh", background: "#0b2747", color: "white", padding: "30px 20px" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="container">
          <motion.h1 className="display-4 fw-bold" initial={{ y: -50 }} animate={{ y: 0 }} transition={{ duration: 1 }}>
            Porvaall Videos
          </motion.h1>
          <motion.p className="lead mt-3" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}>
            Inspiring knowledge. Empowering minds. Connecting ideas.
          </motion.p>
        </div>
      </motion.section>

      {/* VIDEO GRID */}
      <section className="container my-5">
        <div className="row">
          {currentVideos.map((video) => (
            <div className="col-12 col-md-6 col-lg-3 mb-4" key={video.id}>
              <div className="card h-100 shadow-sm">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="card-img-top"
                  style={{ cursor: "pointer", objectFit: "cover", height: "180px" }}
                  onClick={() => setSelectedVideo(video)}
                />
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title">{video.title}</h5>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <nav>
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => goToPage(currentPage - 1)}>
                    Prev
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <li key={num} className={`page-item ${currentPage === num ? "active" : ""}`}>
                    <button className="page-link" onClick={() => goToPage(num)}>
                      {num}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => goToPage(currentPage + 1)}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}

        {/* VIDEO OVERLAY */}
        {selectedVideo && (
          <div className="video-overlay d-flex justify-content-center align-items-center">
            <div className="position-relative" style={{ maxWidth: "900px", width: "90%" }}>
              <button onClick={() => setSelectedVideo(null)}>
                <FaTimes />
              </button>
              <iframe
                width="100%"
                height="500"
                src={selectedVideo.videoUrl + "?autoplay=1"}
                title={selectedVideo.title}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
                style={{ borderRadius: "10px" }}
              ></iframe>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default VideosPage;
